import { VoiceUITool } from './types'

// "Discuss this storyboard" (Adam, 16 Sep 2026): a storyboard is the web
// page the agent builds from a session's tables — `story.html`, a fragment of
// pt-* boxes and prose, with the datasets it names as sibling JSON files.
// Douglas joins the page primed with a summary of it (what the page says,
// section by section, with the figures resolved) and three tools: list the
// datasets, read one as a small grid, and bring a section onto the screen.
//
// Everything here is pure — no React, no fetch — so it is unit-testable on
// its own. The hook that owns the page (useStoryboardDiscussion) fetches the
// fragment and the datasets and hands them in.

// ---------------------------------------------------------------------------
// PlatinumData, the shape every dataset on the page has (a Carbon table or a
// compose_table composite). Only the fields read here are typed.
// ---------------------------------------------------------------------------

export interface StoryboardVec {
  label?: string
  type?: string
}

export interface StoryboardDataset {
  name?: string
  meta?: { top?: string; side?: string; filter?: string; weight?: string }
  top?: { vecs?: StoryboardVec[] }
  side?: { vecs?: StoryboardVec[] }
  cells?: { rows?: Array<{ cell?: Array<Record<string, number | undefined>> }> }
  /** A composite's sidecar: cell "row,col" → { unit }. Absent on a Carbon table. */
  provenance?: { cells?: Record<string, { unit?: string }> }
}

const SKIP_TYPES = new Set(['Base', 'Spacer', 'Empty'])

function norm(s: string | undefined): string {
  return (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
}

function isComposite(d: StoryboardDataset): boolean {
  return !!d.provenance
}

/** Index of the reportable vec whose label matches, or -1. Base/Spacer/Empty never match by label. */
function findVec(vecs: StoryboardVec[] | undefined, label: string): number {
  const want = norm(label)
  if (!want || !vecs) return -1
  return vecs.findIndex((v) => !SKIP_TYPES.has(v.type ?? '') && norm(v.label) === want)
}

/** One figure formatted for speech and for the summary: "15%", "+3 pts", "1,204". */
export function formatFigure(value: number, unit: string, decimals?: number): string {
  const d = decimals ?? (Number.isInteger(Math.round(value * 10) / 10) ? 0 : 1)
  const fixed = (n: number) => n.toFixed(d)
  switch (unit) {
    case 'percent':
      return `${fixed(value)}%`
    case 'pp':
      return `${value > 0 ? '+' : ''}${fixed(value)} pts`
    case 'count':
      return Math.round(value).toLocaleString('en-GB')
    default:
      return fixed(value)
  }
}

/**
 * The value of one cell, resolved the way the page's boxes resolve it: labels
 * matched exactly (case and whitespace aside); metric defaulting to colpc for a
 * Carbon table and freq for a composite; colpc/rowpc scaled from fractions to
 * percent; a composite's unit read from its sidecar.
 */
export function cellFigure(
  data: StoryboardDataset,
  row: string,
  col: string,
  metric?: string,
  decimals?: number,
): string | undefined {
  const r = findVec(data.side?.vecs, row)
  const c = findVec(data.top?.vecs, col)
  if (r < 0 || c < 0) return undefined
  const composite = isComposite(data)
  const m = metric || (composite ? 'freq' : 'colpc')
  const raw = data.cells?.rows?.[r]?.cell?.[c]?.[m]
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined
  let unit = composite ? data.provenance?.cells?.[`${r},${c}`]?.unit ?? 'raw' : m === 'freq' ? 'count' : 'percent'
  let value = raw
  if (!composite && (m === 'colpc' || m === 'rowpc')) value = raw * 100
  if (composite && unit === 'fraction') {
    value = raw * 100
    unit = 'percent'
  }
  return formatFigure(value, unit, decimals)
}

// ---------------------------------------------------------------------------
// A dataset as a small text grid the model can read in one tool result.
// ---------------------------------------------------------------------------

export interface DatasetTextOptions {
  maxRows?: number
  maxCols?: number
}

/** The dataset as text: a heading line, then one line per row with its figures across the columns. */
export function datasetToText(name: string, data: StoryboardDataset, opts: DatasetTextOptions = {}): string {
  const maxRows = opts.maxRows ?? 40
  const maxCols = opts.maxCols ?? 12
  const composite = isComposite(data)
  const metric = composite ? 'freq' : 'colpc'
  const topVecs = data.top?.vecs ?? []
  const sideVecs = data.side?.vecs ?? []
  const colIdx = topVecs.map((v, i) => (SKIP_TYPES.has(v.type ?? '') ? -1 : i)).filter((i) => i >= 0)
  const shownCols = colIdx.slice(0, maxCols)
  const colLabels = shownCols.map((i, n) => topVecs[i].label?.trim() || `Column ${n + 1}`)

  const lines: string[] = []
  const title = data.name?.trim() || name
  const axes = composite
    ? 'composite table'
    : [data.meta?.side && `rows: ${data.meta.side}`, data.meta?.top && `columns: ${data.meta.top}`, data.meta?.filter && `filter: ${data.meta.filter}`]
        .filter(Boolean)
        .join('; ')
  lines.push(`${name}: "${title}"${axes ? ` (${axes})` : ''}`)
  lines.push(`Columns: ${colLabels.join(' | ')}`)

  const baseRow = sideVecs.findIndex((v) => v.type === 'Base')
  if (baseRow >= 0) {
    const cells = data.cells?.rows?.[baseRow]?.cell ?? []
    lines.push(`People asked (base): ${shownCols.map((i) => formatFigure(cells[i]?.freq ?? 0, 'count')).join(' | ')}`)
  }

  let shownRows = 0
  let hiddenRows = 0
  sideVecs.forEach((v, r) => {
    if (SKIP_TYPES.has(v.type ?? '')) return
    if (shownRows >= maxRows) {
      hiddenRows++
      return
    }
    shownRows++
    const cells = data.cells?.rows?.[r]?.cell ?? []
    const figures = shownCols.map((c) => {
      const raw = cells[c]?.[metric]
      if (typeof raw !== 'number' || !Number.isFinite(raw)) return '–'
      if (composite) {
        const unit = data.provenance?.cells?.[`${r},${c}`]?.unit ?? 'raw'
        return unit === 'fraction' ? formatFigure(raw * 100, 'percent') : formatFigure(raw, unit)
      }
      return formatFigure(raw * 100, 'percent')
    })
    lines.push(`${v.label?.trim() || `Row ${r + 1}`}: ${figures.join(' | ')}`)
  })
  if (hiddenRows > 0) lines.push(`(${hiddenRows} more rows not shown)`)
  if (colIdx.length > shownCols.length) lines.push(`(${colIdx.length - shownCols.length} more columns not shown)`)
  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// The storyboard fragment, read into a summary.
// ---------------------------------------------------------------------------

export interface StoryboardKpi {
  label: string
  from: string
  row: string
  col: string
  /** The figure, when the dataset was available. */
  value?: string
  compareLabel?: string
  compareValue?: string
}

export interface StoryboardChart {
  type: string
  from: string
  title?: string
}

export interface StoryboardSection {
  title: string
  /** In a presentation: what the section is, and its deck route (`#/s` or `#/s/l`). */
  kind?: 'slide' | 'layer'
  at?: string
  text: string
  kpis: StoryboardKpi[]
  charts: StoryboardChart[]
  tables: Array<{ from: string; title?: string }>
}

export interface StoryboardSummary {
  title: string
  subtitle: string
  bar: string
  verdict: string
  heroText: string
  heroKpi?: StoryboardKpi
  sections: StoryboardSection[]
  methodology: string
  /** Every dataset the page names, in order of first use. */
  datasets: string[]
  /** The page is a presentation (root pt-deck): its slides and layers are the sections. */
  isDeck?: boolean
}

/** Reads a figure for a `pt-num`/`pt-kpi` address; undefined when the dataset is not loaded or the cell is not found. */
export type FigureLookup = (from: string, row: string, col: string, metric?: string, decimals?: number) => string | undefined

/** The dataset names a fragment refers to, in order of first use. Pure text scan, usable before anything is loaded. */
export function storyboardDatasetNames(html: string): string[] {
  const names: string[] = []
  const re = /\b(?:from|change-from|compare-from)\s*=\s*"([^"]+)"/g
  let m: RegExpExecArray | null
  while ((m = re.exec(html))) {
    const n = m[1].trim()
    if (n && !names.includes(n)) names.push(n)
  }
  return names
}

function attr(el: Element, name: string): string {
  return (el.getAttribute(name) ?? '').trim()
}

function kpiOf(el: Element, lookup: FigureLookup): StoryboardKpi {
  const from = attr(el, 'from')
  const row = attr(el, 'row')
  const col = attr(el, 'col')
  const metric = attr(el, 'metric') || undefined
  const decimals = attr(el, 'decimals') ? Number(attr(el, 'decimals')) : undefined
  const kpi: StoryboardKpi = { label: attr(el, 'label'), from, row, col, value: lookup(from, row, col, metric, decimals) }
  const compareRow = attr(el, 'compare-row')
  const compareCol = attr(el, 'compare-col')
  if (compareRow || compareCol) {
    kpi.compareLabel = attr(el, 'compare-label') || compareRow || compareCol
    kpi.compareValue = lookup(attr(el, 'compare-from') || from, compareRow || row, compareCol || col, metric, decimals)
  }
  return kpi
}

/** Prose with every `pt-num` replaced by its figure, whitespace collapsed. Boxes other than pt-num and pt-text are left out. */
function proseOf(root: Element, lookup: FigureLookup): string {
  const clone = root.cloneNode(true) as Element
  // A slide's or a layer's own words: the layers inside it are sections of their own.
  clone.querySelectorAll('pt-layer').forEach((n) => n.remove())
  clone.querySelectorAll('pt-num').forEach((n) => {
    const figure = lookup(attr(n, 'from'), attr(n, 'row'), attr(n, 'col'), attr(n, 'metric') || undefined, attr(n, 'decimals') ? Number(attr(n, 'decimals')) : undefined)
    n.replaceWith(clone.ownerDocument.createTextNode(figure ?? '[figure]'))
  })
  // A callout's heading and a quote's attribution are attributes: say them,
  // so who said a quote reaches the model with the quote.
  clone.querySelectorAll('pt-callout').forEach((c) => {
    if (attr(c, 'title')) c.prepend(clone.ownerDocument.createTextNode(`${attr(c, 'title')}: `))
    if (attr(c, 'cite')) c.append(clone.ownerDocument.createTextNode(` (${attr(c, 'cite')})`))
  })
  // Structural boxes carry no prose of their own; drop them so a chart's
  // attributes never leak into the text. A picture says nothing about the
  // data, so it goes too.
  clone.querySelectorAll('pt-kpis, pt-kpi, pt-chart, pt-table, pt-figure, pt-sources').forEach((n) => n.remove())
  // List items and paragraphs read as separate sentences.
  clone.querySelectorAll('li, p').forEach((n) => n.append(clone.ownerDocument.createTextNode(' ')))
  return (clone.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * Parse a storyboard fragment into a summary. `lookup` resolves the figures
 * the boxes name; pass one that returns undefined to summarise the structure
 * alone. Needs a DOM (the browser, or jsdom in tests).
 */
export function parseStoryboard(html: string, lookup: FigureLookup): StoryboardSummary {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  const body = doc.body
  const deck = body.querySelector('pt-deck')
  const hero = body.querySelector('pt-hero')
  const heroKpiEl = hero?.querySelector(':scope > pt-kpi') ?? hero?.querySelector('pt-kpi') ?? null

  // A presentation's sections are its slides and layers, in page order, each
  // with only its own boxes; a story page's are its pt-sections.
  const sectionEls = deck ? Array.from(body.querySelectorAll('pt-slide, pt-layer')) : Array.from(body.querySelectorAll('pt-section'))
  const own = (s: Element, selector: string) => Array.from(s.querySelectorAll(selector)).filter((el) => !deck || el.closest('pt-slide, pt-layer') === s)
  const sections: StoryboardSection[] = sectionEls.map((s) => ({
    title: attr(s, 'title'),
    ...(deck ? { kind: s.tagName.toLowerCase() === 'pt-slide' ? 'slide' as const : 'layer' as const, at: deckRouteOf(s) } : {}),
    text: proseOf(s, lookup),
    kpis: own(s, 'pt-kpi').map((k) => kpiOf(k, lookup)),
    charts: own(s, 'pt-chart').map((c) => ({ type: attr(c, 'type'), from: attr(c, 'from'), title: attr(c, 'title') || undefined })),
    tables: own(s, 'pt-table').map((t) => ({ from: attr(t, 'from'), title: attr(t, 'title') || undefined })),
  }))

  const sources = body.querySelector('pt-sources')
  const head = hero ?? deck
  return {
    title: head ? attr(head, 'title') : '',
    subtitle: head ? attr(head, 'subtitle') : '',
    bar: head ? attr(head, 'bar') : '',
    verdict: hero ? attr(hero, 'verdict') : '',
    heroText: hero ? proseOf(hero, lookup) : '',
    heroKpi: heroKpiEl ? kpiOf(heroKpiEl, lookup) : undefined,
    sections,
    methodology: sources ? (sources.textContent ?? '').replace(/\s+/g, ' ').trim() : '',
    datasets: storyboardDatasetNames(html),
    ...(deck ? { isDeck: true } : {}),
  }
}

/**
 * A slide's or a layer's deck route: `#/<slide>` or `#/<slide>/<layer>`.
 * Layer ids are unique in a page, so a nested layer's route names only its
 * slide and itself; the deck opens the outer layers. Undefined when an id is missing.
 */
function deckRouteOf(el: Element): string | undefined {
  const id = attr(el, 'id')
  const slide = el.tagName.toLowerCase() === 'pt-slide' ? el : el.closest('pt-slide')
  const slideId = slide ? attr(slide, 'id') : ''
  if (!id || !slideId) return undefined
  return slide === el ? `#/${slideId}` : `#/${slideId}/${id}`
}

// ---------------------------------------------------------------------------
// The page-context block Douglas is given at connect.
// ---------------------------------------------------------------------------

/** The server caps page context at 12,000 characters; stay under it with room for the tool list. */
export const STORYBOARD_CONTEXT_LIMIT = 11000
const SECTION_TEXT_LIMIT = 700

function clip(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + '…'
}

function kpiLine(k: StoryboardKpi): string {
  const v = k.value ?? '[figure]'
  return k.compareLabel ? `${k.label}: ${v} (${k.compareLabel}: ${k.compareValue ?? '[figure]'})` : `${k.label}: ${v}`
}

/** What the model is told about the storyboard: the headline, the figures, every section in order, the methodology and the datasets. */
export function storyboardPageContext(summary: StoryboardSummary): string {
  const out: string[] = []
  out.push('This page is a storyboard: a web page of findings the agent built from this project\'s survey tables. The person pressed Discuss to talk it through with you.')
  out.push('')
  out.push(`Headline: ${summary.title || '(untitled)'}`)
  if (summary.subtitle) out.push(`Standfirst: ${summary.subtitle}`)
  if (summary.verdict) out.push(`Verdict: ${summary.verdict}`)
  if (summary.heroKpi) out.push(`Hero figure — ${kpiLine(summary.heroKpi)}`)
  if (summary.heroText) out.push(clip(summary.heroText, 1200))
  out.push('')
  out.push(summary.isDeck
    ? 'This storyboard is a presentation: its slides, and the layers that drill into them, in order (show_storyboard_section takes these titles):'
    : 'Sections, in page order (show_storyboard_section takes these titles):')
  summary.sections.forEach((s, i) => {
    const parts: string[] = [`${i + 1}. ${s.kind === 'layer' ? '(layer) ' : ''}${s.title || '(untitled section)'}`]
    if (s.kpis.length) parts.push(`   Figures: ${s.kpis.map(kpiLine).join('; ')}`)
    if (s.text) parts.push(`   ${clip(s.text, SECTION_TEXT_LIMIT)}`)
    s.charts.forEach((c) => parts.push(`   Chart (${c.type}) from dataset ${c.from}${c.title ? `: "${c.title}"` : ''}`))
    s.tables.forEach((t) => parts.push(`   Table from dataset ${t.from}${t.title ? `: "${t.title}"` : ''}`))
    out.push(parts.join('\n'))
  })
  if (summary.methodology) {
    out.push('')
    out.push(`Methodology: ${clip(summary.methodology, 600)}`)
  }
  out.push('')
  out.push(`Datasets on the page (read_storyboard_dataset shows the numbers): ${summary.datasets.join(', ') || 'none'}`)
  const text = out.join('\n')
  return text.length <= STORYBOARD_CONTEXT_LIMIT ? text : clip(text, STORYBOARD_CONTEXT_LIMIT)
}

// ---------------------------------------------------------------------------
// The tools.
// ---------------------------------------------------------------------------

export interface StoryboardToolDeps {
  /** The parsed page, or null while it loads. Read live. */
  summary: () => StoryboardSummary | null
  /** A dataset by name, fetched and cached by the owner; null when there is no such file. */
  loadDataset: (name: string) => Promise<StoryboardDataset | null>
  /** Brings a section on screen (scrolls a story page; moves a presentation there); returns the title actually shown, or null when nothing matched. */
  showSection: (title: string) => string | null
}

const ok = (extra: Record<string, unknown>) => JSON.stringify({ ok: true, ...extra })
const err = (message: string) => JSON.stringify({ error: message })

/** Best match for a spoken section title: exact, then starts-with, then contains, then most shared words. */
export function matchSectionTitle(query: string, titles: string[]): string | null {
  const q = norm(query)
  if (!q) return null
  const exact = titles.find((t) => norm(t) === q)
  if (exact) return exact
  const starts = titles.find((t) => norm(t).startsWith(q))
  if (starts) return starts
  const contains = titles.find((t) => norm(t).includes(q) || q.includes(norm(t)))
  if (contains) return contains
  // Spoken titles come back inflected ("recommendations" for "recommend"),
  // so two words match when one begins the other, at four letters or more.
  const words = q.split(' ').filter((w) => w.length > 2)
  const sameWord = (a: string, b: string) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)))
  let best: { t: string; n: number } | null = null
  for (const t of titles) {
    const n = norm(t)
      .split(' ')
      .filter((w) => words.some((qw) => sameWord(qw, w))).length
    if (n > 0 && (!best || n > best.n)) best = { t, n }
  }
  return best?.t ?? null
}

export function buildStoryboardVoiceTools(deps: StoryboardToolDeps): VoiceUITool[] {
  return [
    {
      name: 'list_storyboard_datasets',
      description: 'List the datasets (tables) this storyboard is built from, with which sections use each. Call it to find the right dataset name before read_storyboard_dataset.',
      meta: { category: 'read' },
      parameters: { type: 'object', properties: {}, additionalProperties: false },
      execute: () => {
        const s = deps.summary()
        if (!s) return err('the storyboard is still loading')
        const uses = new Map<string, Set<string>>()
        const add = (name: string, where: string) => {
          if (!uses.has(name)) uses.set(name, new Set())
          uses.get(name)!.add(where)
        }
        if (s.heroKpi) add(s.heroKpi.from, 'hero')
        s.sections.forEach((sec) => {
          sec.kpis.forEach((k) => add(k.from, sec.title))
          sec.charts.forEach((c) => add(c.from, sec.title))
          sec.tables.forEach((t) => add(t.from, sec.title))
        })
        return JSON.stringify({
          datasets: s.datasets.map((name) => ({ name, used_in: Array.from(uses.get(name) ?? []) })),
        })
      },
    },
    {
      name: 'read_storyboard_dataset',
      description: 'Read one of the storyboard\'s datasets as a small grid of figures (rows by columns, percentages of people asked unless the grid says otherwise). Call this before quoting any number that is not already in the page summary. Never guess a figure.',
      meta: { category: 'read' },
      parameters: {
        type: 'object',
        properties: { name: { type: 'string', description: 'The dataset name, exactly as listed on the page (e.g. visibility_gap).' } },
        required: ['name'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const name = String(args.name ?? '').trim()
        if (!name) return err('name is required')
        try {
          const data = await deps.loadDataset(name)
          if (!data) {
            const known = deps.summary()?.datasets ?? []
            return err(`no dataset called "${name}" on this page${known.length ? `; the page has: ${known.join(', ')}` : ''}`)
          }
          return datasetToText(name, data)
        } catch (e) {
          return err(e instanceof Error ? e.message : `could not read ${name}`)
        }
      },
    },
    {
      name: 'show_storyboard_section',
      description: 'Scroll the storyboard so a section is on screen. Pass the section title from the page summary (a close match is fine, or "top" for the headline). Call it before you talk about a section, so the person is looking at what you mean.',
      meta: { category: 'action' },
      parameters: {
        type: 'object',
        properties: { title: { type: 'string', description: 'The section title, or "top".' } },
        required: ['title'],
        additionalProperties: false,
      },
      execute: (args) => {
        const title = String(args.title ?? '').trim()
        if (!title) return err('title is required')
        const s = deps.summary()
        if (!s) return err('the storyboard is still loading')
        const wantTop = ['top', 'hero', 'headline', 'start'].includes(norm(title))
        const target = wantTop ? 'top' : matchSectionTitle(title, s.sections.map((x) => x.title))
        if (!target) return err(`no section like "${title}"; the sections are: ${s.sections.map((x) => x.title).join('; ')}`)
        const shown = deps.showSection(target)
        if (!shown) return err(`could not scroll to "${target}"`)
        const section = s.sections.find((x) => x.title === target)
        return ok({ showing: shown, text: section ? clip(section.text, 400) : clip(s.heroText, 400) })
      },
    },
  ]
}
